/**
 * Windows of CD4's own that a part of the page moves into: the video view's pop-out window in the Communicator Desktop app,
 * and the drawing window. The element moves - Vue keeps rendering it from the main page - so the window only needs an empty
 * page on CD4's own address and the app's styles.
 */

import { sleep } from '@/utilities/Tools'

/** The page such a window loads: empty, and on CD4's own address, the only one the Communicator Desktop app opens in a window */
export const POPOUT_PAGE = 'video-popout.html'

/** How long a window may take to load its page */
const POPOUT_LOAD_TIMEOUT_MS = 5_000

export interface PopoutOptions {
	width: number
	height: number
	/** The smallest the Communicator Desktop app lets the window be made - a browser ignores it */
	minWidth: number
	minHeight: number
	/** Keep the window above other windows - in the Communicator Desktop app only */
	alwaysOnTop?: boolean
}

/**
 * Has the window got its own page yet
 * @param win The window
 */
function popoutLoaded(win: Window) {
	try {
		return win.location.pathname.endsWith(POPOUT_PAGE) && win.document.readyState === 'complete'
	} catch (_) {
		return false // Between documents
	}
}

/**
 * Open a window on POPOUT_PAGE, and wait until it shows that page - window.open hands the window back while it still holds
 * about:blank
 * @param name The window's name - opening the same name again reuses the window
 * @param options Size and behaviour
 * @returns The window
 */
export async function openPopout(name: string, options: PopoutOptions): Promise<Window> {
	const url = new URL(import.meta.env.BASE_URL + POPOUT_PAGE, location.origin).href
	const width = Math.max(options.width, options.minWidth)
	const height = Math.max(options.height, options.minHeight)
	const features = [ 'popup', `width=${width}`, `height=${height}`, `minWidth=${options.minWidth}`, `minHeight=${options.minHeight}` ]

	if (options.alwaysOnTop) { features.push('alwaysOnTop=yes') }

	const win = window.open(url, name, features.join(','))

	if (!win) { throw new Error('the window was not opened') }
	const started = Date.now()

	while (!popoutLoaded(win)) {
		if (win.closed || Date.now() - started > POPOUT_LOAD_TIMEOUT_MS) {
			win.close()
			throw new Error('the window did not load its page')
		}
		await sleep(50)
	}
	return win
}

/**
 * Give a window the app's styles
 * @param target The window's document
 */
function copyStyles(target: Document) {
	for (const sheet of Array.from(document.styleSheets)) {
		try {
			const style = target.createElement('style')

			style.textContent = Array.from(sheet.cssRules, rule => rule.cssText).join('\n')
			target.head.append(style)
		} catch (_) {
			// A sheet from another origin (the theme, from the API) can't be read: link it instead
			if (sheet.href) {
				const link = target.createElement('link')

				link.rel = 'stylesheet'
				link.href = sheet.href
				target.head.append(link)
			}
		}
	}
}

/**
 * Give a window the app's styles, and keep giving it the styles added after it opened: PrimeVue adds a component's styles
 * to the main document the first time the component mounts - which may be in the window
 * @param target The window's document
 * @returns Stops following the main document's styles
 */
export function shareStyles(target: Document): () => void {
	copyStyles(target)

	const mirror = new MutationObserver(records => {
		for (const record of records) {
			for (const node of Array.from(record.addedNodes)) {
				if (node instanceof HTMLStyleElement || (node instanceof HTMLLinkElement && node.rel === 'stylesheet')) {
					target.head.append(target.importNode(node, true))
				}
			}
		}
	})

	mirror.observe(document.head, { childList: true })
	return () => { mirror.disconnect() }
}

/**
 * Carry on playing after a move between documents
 * @param element The element moved
 */
export function resumeVideos(element: HTMLElement) {
	for (const video of Array.from(element.querySelectorAll('video'))) {
		video.play().catch(() => { /* a muted video may always play - a refusal just leaves the last frame */ })
	}
}
